// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { fetchTeacherSubjectsWithStudents, fetchMessagingSummary } from '../../services/api';
import { isReadOnlyTeacherDemo } from '../../utils/demoAccess';
import GradingColorReference from '../../components/GradingColorReference';
import { useUnreadMessagingSummary } from '../../utils/messageReadState';
import { CALENDAR_CATEGORIES, fetchUnifiedCalendarEvents } from '../../services/teacherCalendar';
import './Home.css';

const S = {
  shortcut: { background: '#dbeafe', color: '#1e3a5f', border: '1px solid #bfdbfe', borderRadius: 10, padding: '9px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer', boxShadow: 'var(--ae-shadow-card)' },
  pageTitle: { fontSize: 28, fontWeight: 800, color: 'var(--ae-primary-strong)', marginBottom: 20 },
  pageSubtitle: { color: 'var(--ae-text-muted)', fontSize: 14, marginBottom: 18 },
  gradeBadge: { display: 'inline-flex', alignItems: 'center', gap: 8, background: '#b45309', color: '#ffffff', borderRadius: 20, padding: '4px 16px', fontSize: 13, fontWeight: 700, marginBottom: 12 },
  classCard: { background: 'var(--ae-surface)', border: '1px solid var(--ae-border)', borderRadius: 14, padding: '16px 18px', cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s', boxShadow: 'var(--ae-shadow-card)', minHeight: 154, display: 'flex', flexDirection: 'column' },
  classCardHover: { border: '1px solid #a7bde8', boxShadow: '0 10px 24px rgba(24, 56, 116, 0.14)', transform: 'translateY(-2px)' },
  classCardSelected: { border: '1px solid #3f67b7', boxShadow: '0 10px 24px rgba(24, 56, 116, 0.14)', background: '#edf3ff' },
  classId: { fontSize: 14, fontWeight: 800, color: 'var(--ae-primary-strong)', marginBottom: 6, lineHeight: 1.35, overflowWrap: 'anywhere' },
  classCount: { display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--ae-accent-soft)', color: '#7c2d12', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700 },
  studentNames: { marginTop: 8, fontSize: 11, color: 'var(--ae-text-muted)', lineHeight: 1.6 },
  classGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 14, alignItems: 'stretch' },
};

const formatCalendarDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const calendarMonthDays = (monthDate) => {
  const firstDay = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  firstDay.setDate(firstDay.getDate() - firstDay.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(firstDay);
    date.setDate(firstDay.getDate() + index);
    return date;
  });
};

export default function TeacherHome() {
  const { user } = useAuth();
  const readOnlyDemo = isReadOnlyTeacherDemo(user);
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState<any>([]);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');
  // Try to restore selected tile from sessionStorage
  const [selectedTileKey, setSelectedTileKey] = useState(() => sessionStorage.getItem('teacherHome.selectedTileKey') || '');
  const [hoveredCard, setHoveredCard] = useState<any>('');
  const [rawMessagingSummary, setMessagingSummary] = useState<any>(null);
  const messagingSummary = useUnreadMessagingSummary(rawMessagingSummary, user);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(() => formatCalendarDate(new Date()));
  const [calendarEvents, setCalendarEvents] = useState<any>([]);
  const [calendarLoading, setCalendarLoading] = useState<any>(true);
  const [calendarError, setCalendarError] = useState<any>('');
  const teacherClasses = useMemo(() => subjects.flatMap(subject =>
    (subject.classes || []).map(cls => ({
      class_id: String(cls.class_id || cls.id),
      class_name: cls.class_name || cls.name || `${subject.subject_name || 'Class'} - Class ${cls.class_id}`,
      subject_name: subject.subject_name || subject.subject || 'General',
      students: cls.students || [],
    }))
  ), [subjects]);
  const calendarDays = useMemo(() => calendarMonthDays(calendarMonth), [calendarMonth]);
  const selectedDayEvents = useMemo(
    () => calendarEvents.filter(event => event.date === selectedCalendarDate),
    [calendarEvents, selectedCalendarDate],
  );

  useEffect(() => {
    setLoading(true);
    setError('');
    fetchTeacherSubjectsWithStudents()
      .then(data => setSubjects(data.subjects || []))
      .catch(() => setError('Could not load subjects.'))
      .finally(() => setLoading(false));

    if (typeof fetchMessagingSummary === 'function') {
      fetchMessagingSummary()
        .then(data => setMessagingSummary(data))
        .catch(() => setMessagingSummary(null));
    }
  }, []);

  useEffect(() => {
    if (loading) return undefined;
    let active = true;
    const days = calendarMonthDays(calendarMonth);
    setCalendarLoading(true);
    setCalendarError('');
    fetchUnifiedCalendarEvents({
      startDate: formatCalendarDate(days[0]),
      endDate: formatCalendarDate(days[days.length - 1]),
      teacherId: user?.user_id || user?.id || null,
      schoolId: user?.school_id,
      teacherClasses,
    })
      .then(data => {
        if (active) setCalendarEvents(data);
      })
      .catch(error => {
        if (active) setCalendarError(error.message || 'Could not load calendar events.');
      })
      .finally(() => {
        if (active) setCalendarLoading(false);
      });
    return () => { active = false; };
  }, [calendarMonth, loading, teacherClasses, user?.id, user?.school_id, user?.user_id]);

  // Save selected tile to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('teacherHome.selectedTileKey', selectedTileKey);
  }, [selectedTileKey]);

  if (loading) return <div style={{ padding: 24, color: '#6b7280', textAlign: 'center' }}>Loading...</div>;

  if (error) return (
    <div style={{ padding: 24 }}>
      <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 12, padding: '16px 20px', color: '#b91c1c', fontWeight: 600 }}>{error}</div>
    </div>
  );

  const userName = user?.first_name ? `${user.first_name}${user.last_name ? ' ' + user.last_name : ''}` : (user?.name || user?.email || '');
  return (
    <div>
      <h1 style={{ ...S.pageTitle, marginTop: 0 }}>{`Welcome${userName ? ', ' + userName : ''}!`}</h1>
      <div style={S.pageSubtitle}>{readOnlyDemo
        ? 'Explore student profiles and academic progress.'
        : 'Use your calendar and resource links to plan work, review progress, and open class tools.'}</div>

      {/* Messages Spot on Dashboard */}
      {!readOnlyDemo && (
        messagingSummary && messagingSummary.incoming_count > 0 ? (
          <section
            aria-label="New Messages from Families"
            style={{
              background: '#f0f7ff',
              border: '1.5px solid #93c5fd',
              borderRadius: 14,
              padding: '16px 20px',
              marginBottom: 22,
              boxShadow: '0 4px 14px rgba(37, 99, 235, 0.08)',
            }}
            data-testid="teacher-dashboard-messages-spot"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#dbeafe', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                  💬
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <h2 style={{ fontSize: 16, fontWeight: 800, color: '#1e3a5f', margin: 0 }}>
                      New Messages from Families
                    </h2>
                    <span style={{ fontSize: 11, fontWeight: 700, background: '#2563eb', color: '#ffffff', borderRadius: 999, padding: '2px 8px' }}>
                      {messagingSummary.incoming_count} {messagingSummary.incoming_count === 1 ? 'new' : 'new'}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    Recent messages and updates from connected parents and students.
                  </div>
                </div>
              </div>
              <button
                type="button"
                style={{ ...S.shortcut, background: '#2563eb', color: '#ffffff', border: '1px solid #1d4ed8' }}
                onClick={() => navigate('/teacher/connect')}
              >
                Open Messages →
              </button>
            </div>

            <div style={{ display: 'grid', gap: 8 }}>
              {(messagingSummary.recent_messages || []).slice(0, 3).map((msg) => (
                <div
                  key={msg.message_id}
                  role="button"
                  tabIndex={0}
                  style={{
                    background: '#ffffff',
                    border: '1px solid #bfdbfe',
                    borderRadius: 10,
                    padding: '10px 14px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'background 0.15s, border-color 0.15s',
                    gap: 12,
                  }}
                  onClick={() => navigate(`/teacher/connect?participant_id=${msg.sender_id}&student_id=${msg.student_id || ''}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      navigate(`/teacher/connect?participant_id=${msg.sender_id}&student_id=${msg.student_id || ''}`);
                    }
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: '#1e3a5f' }}>{msg.sender_name}</span>
                      {msg.student_name && (
                        <span style={{ fontSize: 11, background: '#e0f2fe', color: '#0369a1', padding: '1px 7px', borderRadius: 6, fontWeight: 600 }}>
                          {msg.student_name}
                        </span>
                      )}
                      {msg.sent_at && (
                        <span style={{ fontSize: 11, color: '#64748b' }}>
                          {new Date(msg.sent_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}{' '}
                          {new Date(msg.sent_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      &ldquo;{msg.body}&rdquo;
                    </div>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#2563eb', whiteSpace: 'nowrap' }}>
                    Reply →
                  </span>
                </div>
              ))}
            </div>
          </section>
        ) : (
          <section
            aria-label="Family Messages"
            style={{
              background: '#f8fafc',
              border: '1px dashed #cbd5e1',
              borderRadius: 12,
              padding: '12px 18px',
              marginBottom: 22,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 10,
            }}
            data-testid="teacher-dashboard-messages-spot"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18 }} aria-hidden="true">💬</span>
              <div>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f' }}>Family Messages: </span>
                <span style={{ fontSize: 13, color: '#64748b' }}>No new messages right now. Connect with families anytime.</span>
              </div>
            </div>
            <button
              type="button"
              style={{ ...S.shortcut, padding: '6px 14px', fontSize: 12 }}
              onClick={() => navigate('/teacher/connect')}
            >
              Open Connect
            </button>
          </section>
        )
      )}

      <section className="teacher-home-workspace" aria-label="Calendar and teacher resources">
        <div className="teacher-home-calendar-card">
          <div className="teacher-home-panel-heading">
            <div>
              <h2>Calendar</h2>
              <p>Upcoming classwork, events, and reminders</p>
            </div>
            <button type="button" className="teacher-home-text-link" onClick={() => navigate('/teacher/calendar')}>
              Open Calendar →
            </button>
          </div>
          <div className="teacher-home-calendar-controls">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => {
                const previous = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1);
                setCalendarMonth(previous);
                setSelectedCalendarDate(formatCalendarDate(previous));
              }}
            >‹</button>
            <strong>{calendarMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</strong>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => {
                const next = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1);
                setCalendarMonth(next);
                setSelectedCalendarDate(formatCalendarDate(next));
              }}
            >›</button>
          </div>
          <div className="teacher-home-calendar-grid" aria-label="Month dates">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
              <span className="teacher-home-calendar-weekday" key={`${day}-${index}`}>{day}</span>
            ))}
            {calendarDays.map(date => {
              const iso = formatCalendarDate(date);
              const dayEvents = calendarEvents.filter(event => event.date === iso);
              const categories = [...new Set(dayEvents.map(event => event.category))].slice(0, 3);
              const isCurrentMonth = date.getMonth() === calendarMonth.getMonth();
              const isToday = iso === formatCalendarDate(new Date());
              return (
                <button
                  key={iso}
                  type="button"
                  className={`teacher-home-calendar-day${isCurrentMonth ? '' : ' is-outside-month'}${isToday ? ' is-today' : ''}${selectedCalendarDate === iso ? ' is-selected' : ''}`}
                  aria-label={`${date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}${dayEvents.length ? `, ${dayEvents.length} calendar ${dayEvents.length === 1 ? 'item' : 'items'}` : ''}`}
                  aria-pressed={selectedCalendarDate === iso}
                  onClick={() => setSelectedCalendarDate(iso)}
                >
                  <span>{date.getDate()}</span>
                  {categories.length > 0 && (
                    <span className="teacher-home-calendar-dots" aria-hidden="true">
                      {categories.map(category => (
                        <i key={category} style={{ backgroundColor: CALENDAR_CATEGORIES[category]?.color || '#64748b' }} />
                      ))}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="teacher-home-day-events" aria-live="polite">
            <div className="teacher-home-day-heading">
              {new Date(`${selectedCalendarDate}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
              {calendarLoading && <span>Loading…</span>}
            </div>
            {calendarError ? (
              <p role="alert" className="teacher-home-calendar-message">{calendarError}</p>
            ) : !calendarLoading && selectedDayEvents.length === 0 ? (
              <p className="teacher-home-calendar-message">No events scheduled.</p>
            ) : (
              selectedDayEvents.slice(0, 3).map(event => (
                <button
                  type="button"
                  key={event.id}
                  className="teacher-home-event-link"
                  onClick={() => {
                    if (event.actionLink && event.actionState) navigate(event.actionLink, { state: event.actionState });
                    else navigate(event.actionLink || '/teacher/calendar');
                  }}
                >
                  <i style={{ backgroundColor: CALENDAR_CATEGORIES[event.category]?.color || '#64748b' }} />
                  <span>{event.title}</span>
                </button>
              ))
            )}
            {selectedDayEvents.length > 3 && (
              <button type="button" className="teacher-home-text-link" onClick={() => navigate('/teacher/calendar')}>
                View {selectedDayEvents.length - 3} more →
              </button>
            )}
          </div>
        </div>

        <div className="teacher-home-resource-panel">
          <div className="teacher-home-panel-heading">
            <div>
              <h2>Teacher Resources</h2>
              <p>Quick access to the tools you use most</p>
            </div>
          </div>
          <div className={`teacher-home-resource-grid${readOnlyDemo ? ' is-demo' : ''}`}>
            {[
              {
                title: 'Student Profiles',
                description: 'Review student learning profiles and progress.',
                label: '👥 Student Profiles',
                path: '/teacher/student-profiles',
              },
              {
                title: 'Learning Tasks',
                description: 'Create, manage, and score learning tasks.',
                label: '📝 Learning Tasks',
                path: '/teacher/assignments',
                extra: { label: '＋ Create Learning Task', path: '/teacher/assignments/create' },
              },
              {
                title: 'Data Points Report',
                description: 'Review recorded progress across standards.',
                label: '📊 Data Points Report',
                path: '/teacher/data-points-report',
                extra: { label: 'Enter Data Points', path: '/teacher/standards-progression' },
              },
              {
                title: 'Training & Resources',
                description: 'Find guidance and professional learning.',
                label: '📚 Training & Resources',
                path: '/teacher/training-resources',
              },
            ].filter(resource => !readOnlyDemo || resource.path === '/teacher/student-profiles').map(resource => (
              <article className="teacher-home-resource-card" key={resource.path}>
                <h3>{resource.title}</h3>
                <p>{resource.description}</p>
                <div className="teacher-home-resource-actions">
                  <button type="button" style={S.shortcut} onClick={() => navigate(resource.path)}>
                    {resource.label}
                  </button>
                  {resource.extra && (
                    <button
                      type="button"
                      className="teacher-home-text-link"
                      onClick={() => navigate(resource.extra.path)}
                    >
                      {resource.extra.label} →
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div style={{ marginBottom: 22 }}>
        <GradingColorReference showPercentages introduction="Use this reference when entering data points and reviewing student learning progress." />
      </div>

      {subjects.length === 0 ? (
        <div style={{ background: 'white', border: '1.5px solid #e0e7ef', borderRadius: 14, padding: '24px 28px', color: '#6b7280', fontSize: 14, boxShadow: '0 1px 4px #0001' }}>
          No subjects found. Ask your admin to assign you to a class.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))', alignItems: 'start', gap: 20 }}>
          {subjects.map(subject => (
            <div key={subject.subject_id}>
              <div style={S.gradeBadge}>📚 {subject.subject_name}</div>
              {subject.classes.length === 0 ? (
                <div style={{ color: '#6b7280', fontSize: 13 }}>No classes for this subject.</div>
              ) : (
                <div style={S.classGrid}>
                  {subject.classes.map(cls => {
                    const tileKey = `${subject.subject_name}-${cls.class_id}`;
                    const isSelected = selectedTileKey === tileKey;
                    const isHovered = hoveredCard === tileKey;
                    const students = cls.students || [];

                    return (
                      <button
                        key={cls.class_id}
                        type="button"
                        style={{
                          ...S.classCard,
                          ...(isSelected ? S.classCardSelected : isHovered ? S.classCardHover : {}),
                        }}
                        onClick={() => setSelectedTileKey(isSelected ? '' : tileKey)}
                        onMouseEnter={() => setHoveredCard(tileKey)}
                        onMouseLeave={() => setHoveredCard('')}
                      >
                        <div style={S.classId}>
                          {cls.class_name ? cls.class_name : `Class ${cls.class_id}`}
                          {cls.class_name ? <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 12, marginLeft: 6 }}>#{cls.class_id}</span> : ''}
                        </div>
                        <div><span style={S.classCount}>👥 {students.length} student{students.length !== 1 ? 's' : ''}</span></div>
                        {students.length > 0 && (
                          <div style={S.studentNames}>
                            {students.slice(0, 4).map(stu => stu.first_name ? `${stu.first_name} ${stu.last_name || ''}`.trim() : `ID:${stu.student_id}`).join(' • ')}
                            {students.length > 4 ? ` • +${students.length - 4} more` : ''}
                          </div>
                        )}

                        {isSelected && (
                          <div className="ae-action-row" style={{ marginTop: 12 }}>
                            <>
                              <button
                                type="button"
                                className="ae-btn-primary"
                                style={{ padding: '6px 14px', fontSize: 12 }}
                                onClick={e => {
                                  e.stopPropagation();
                                  navigate('/teacher/standards-progression', {
                                    state: {
                                      prefill: {
                                        grade: students[0]?.grade_level?.toString(),
                                        subject: subject.subject_name,
                                        classId: cls.class_id,
                                      },
                                      fromHome: true,
                                      homeState: { selectedTileKey },
                                    },
                                  });
                                }}
                              >
                                📝 Enter New Data Point(s)
                              </button>
                              {!readOnlyDemo && <button
                                type="button"
                                style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '6px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer', lineHeight: 1.2 }}
                                onClick={e => {
                                  e.stopPropagation();
                                  navigate('/teacher/assignments', {
                                    state: {
                                      selectedClassId: String(cls.class_id),
                                      selectedSubject: subject.subject_name,
                                      fromHome: true,
                                    },
                                  });
                                }}
                              >
                                📊 Learning Tasks
                              </button>}
                            </>
                            <button
                              type="button"
                              className="ae-btn-soft"
                              style={{ padding: '6px 14px', fontSize: 12 }}
                              onClick={e => {
                                e.stopPropagation();
                                navigate(`/teacher/student-profiles/${cls.class_id}/${encodeURIComponent(subject.subject_name)}`, {
                                  state: { fromHome: true, homeState: { selectedTileKey } },
                                });
                              }}
                            >
                              👥 Open Student Profiles
                            </button>
                            {!readOnlyDemo && <>
                            <button
                              type="button"
                              style={{ background: 'var(--ae-accent)', color: 'white', border: 'none', borderRadius: 8, padding: '6px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer', lineHeight: 1.2 }}
                              onClick={e => {
                                e.stopPropagation();
                                navigate(`/teacher/class-groups/${cls.class_id}`, {
                                  state: { fromHome: true, homeState: { selectedTileKey } },
                                });
                              }}
                            >
                              🧑‍🤝‍🧑 Groups
                            </button>
                            <button
                              type="button"
                              style={{ background: '#0f766e', color: 'white', border: 'none', borderRadius: 8, padding: '6px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer', lineHeight: 1.2 }}
                              onClick={e => {
                                e.stopPropagation();
                                navigate(`/teacher/class-manage/${cls.class_id}`, {
                                  state: { returnTo: { pathname: '/teacher/home' } },
                                });
                              }}
                            >
                              ⚙️ Manage Class
                            </button>
                            <button
                              type="button"
                              style={{ background: '#1f7d6d', color: 'white', border: 'none', borderRadius: 8, padding: '6px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer', lineHeight: 1.2 }}
                              onClick={e => {
                                e.stopPropagation();
                                navigate(`/teacher/bulk-reports/${cls.class_id}`, {
                                  state: { fromHome: true, homeState: { selectedTileKey } },
                                });
                              }}
                            >
                              📥 Download Reports
                            </button>
                            </>}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
